import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type { QueryResult } from '@atlassian/jira-relay-apollo-migration-utils/src/services/use-query/index.tsx';
import type {
	historicalCycleTimeRelayCycleTimeReportQuery$data,
	historicalCycleTimeRelayCycleTimeReportQuery$variables,
} from '@atlassian/jira-relay/src/__generated__/historicalCycleTimeRelayCycleTimeReportQuery.graphql';
import type { CloudId, ProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { ServiceQueryResultWithFetch } from '@atlassian/jira-software-deployments-service-query-types/src/services/types.tsx';
import type { DateRange, TopFilter } from '../../common/types';
import { getWeekEndDateExclusive } from '../../common/utils/date-range';
import { getMaybeCombinedError } from '../../common/utils/errors';
import { toServiceShapeTopFilter } from '../../common/utils/issue-filters';
import type { BarChartData, BarChartBarData } from './types';

export const buildQueryVariables = (
	cloudId: CloudId,
	projectId: ProjectId | null,
	twelveWeeksDateRange: DateRange,
	filters: TopFilter,
): historicalCycleTimeRelayCycleTimeReportQuery$variables => ({
	filter: {
		cloudId,
		resolution: { value: 1, unit: 'WEEK' },
		startFromInclusive: twelveWeeksDateRange.startDateInclusive.toISOString(),
		endAtExclusive: twelveWeeksDateRange.endDateExclusive.toISOString(),
		jiraProjectIds: projectId === null ? [] : [projectId], // Query will be anyway skipped when projectId is null
		issueFilters: toServiceShapeTopFilter(cloudId, filters),
		timezoneId:
			Intl.DateTimeFormat().resolvedOptions().timeZone !== 'Etc/Unknown'
				? Intl.DateTimeFormat().resolvedOptions().timeZone
				: null,
	},
	isIncludeCycleTimeMean: null,
});

// Data is recognized as not meaningful to have statistics comparison if we have less than any 4 weeks data.
export const hasEnoughDataForMeaningfulStatistics = (barChartBarData: BarChartBarData[]): boolean =>
	barChartBarData.reduce(
		(count, dataPoint) =>
			dataPoint.cycleTimeStatistics !== null && dataPoint.cycleTimeStatistics > 0
				? count + 1
				: count,
		0,
	) >= 4;

// @ts-expect-error - TS7006 - Parameter 'rawData' implicitly has an 'any' type.
const cycleTimeMetricsToBarChartBarData = (rawData): BarChartBarData[] =>
	// @ts-expect-error - TS7006 - Parameter 'x' implicitly has an 'any' type.
	rawData.map((x) => {
		// As long as our query is not changed, this condition shouldn't happen. This condition makes Flow happy
		if (x == null || x.dateTime == null) {
			throw new Error(
				`Cycle time report: all data or dateTime in data is missing. data: ${JSON.stringify(
					rawData,
				)}, iteration item: ${x == null ? 'x == null' : JSON.stringify(x)}`,
			);
		}

		const { dateTime, cycleTimeSeconds, issuesShippedCount } = x;
		const startDateInclusive = new Date(dateTime);

		return {
			startDateInclusive,
			endDateExclusive: getWeekEndDateExclusive(startDateInclusive),
			cycleTimeStatistics: cycleTimeSeconds ?? 0,
			numIssues: issuesShippedCount ?? 0,
		};
	});

const getValidatedData = (
	queryResult: QueryResult<
		historicalCycleTimeRelayCycleTimeReportQuery$variables,
		historicalCycleTimeRelayCycleTimeReportQuery$data
	>,
): BarChartData => {
	const cycleTimeMetrics = queryResult.data?.devOpsMetrics?.cycleTime?.cycleTimeMetrics;
	const hasPermissionForAllContributingIssues =
		queryResult.data?.devOpsMetrics?.cycleTime?.hasPermissionForAllContributingIssues;

	// This is when something is unexpectedly wrong
	if (cycleTimeMetrics == null) {
		throw Error(
			`Cycle time report: cycleTimeMetrics is missing ${
				queryResult.error ? `, ${queryResult.error.message}` : ''
			}`,
		);
	}

	if (hasPermissionForAllContributingIssues == null) {
		throw Error(
			`Cycle time report: hasPermissionForAllContributingIssues is missing ${
				queryResult.error ? `, ${queryResult.error.message}` : ''
			}`,
		);
	}

	// As long as our query is not changed, this condition shouldn't happen. This condition makes Flow happy
	if (cycleTimeMetrics[0] == null || cycleTimeMetrics[0].data == null) {
		throw Error(
			`Cycle time report: cycleTimeMetrics data is missing${
				queryResult.error ? `, ${queryResult.error.message}` : ''
			}: ${cycleTimeMetrics[0] ? JSON.stringify(cycleTimeMetrics[0]) : ''}`,
		);
	}

	const { data, aggregateData } = cycleTimeMetrics[0];

	// instead of throwing an error, simply log it. a default value of 0 will be shown in the ui.
	// in practice issuesShippedCount should never be null or undefined.
	// PIR action for https://ops.internal.atlassian.com/jira/browse/PIR-11291
	if (data.filter(Boolean).some(({ issuesShippedCount }) => issuesShippedCount == null)) {
		fireErrorAnalytics({
			error: new Error('Cycle time report: issuesShippedCount is missing'),
			meta: {
				id: 'issuesShippedCountMissing',
				packageName: 'jiraSoftwareCycleTimeBreakdownReport',
				teamName: 'fusion-solaris',
			},
		});
	}

	const barData = cycleTimeMetricsToBarChartBarData(data);

	return {
		barData,
		hasEnoughDataForMeaningfulStatistics: hasEnoughDataForMeaningfulStatistics(barData),
		wholePeriodStatistics: aggregateData ?? 0,
		hasPermissionForAllContributingIssues,
	};
};

export const transformResult = (
	queryResult: QueryResult<
		historicalCycleTimeRelayCycleTimeReportQuery$variables,
		historicalCycleTimeRelayCycleTimeReportQuery$data
	>,
): ServiceQueryResultWithFetch<BarChartData> => {
	const resultBase = {
		loading: queryResult.loading,
		error: queryResult.error,
		fetch: queryResult.refetch,
	};

	if (queryResult.loading) {
		return resultBase;
	}

	try {
		return {
			...resultBase,
			data: getValidatedData(queryResult),
		};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		return {
			...resultBase,
			error: getMaybeCombinedError([queryResult.error, error]),
		};
	}
};

export const isDataEmpty = (barChartBarData: BarChartBarData[]): boolean =>
	!barChartBarData.some((x) => x.cycleTimeStatistics !== 0);

type SelectedBarChartBarDataInfo = {
	barData: BarChartBarData;
	index: number;
};

export const getSelectedBarChartBarDataAndItsIndex = (
	barDataArr: BarChartBarData[],
	selectedDateRange: DateRange,
): SelectedBarChartBarDataInfo => {
	let index = -1;
	barDataArr.forEach((x, i) => {
		if (
			x.startDateInclusive.getTime() === selectedDateRange.startDateInclusive.getTime() &&
			x.endDateExclusive.getTime() === selectedDateRange.endDateExclusive.getTime()
		) {
			index = i;
		}
	});

	if (index === -1) {
		throw new Error('Could not find the bar data for the given selected date range.');
	}

	return {
		barData: barDataArr[index],
		index,
	};
};
